import { useState } from 'react';

import type { ReplacementItem } from '@/chat/types';
import { AudioTypeSelector } from '@/music/components/audio-type-selector';
import { DownloadButton } from '@/music/components/download-button';
import { formatTime } from '@/music/format-time';
import { PlayPauseButton } from '@/music/components/play-pause-button';
import { useAudioPlayerStore } from '@/music/hooks/use-audio-player-store';
import { isSameTrack, type PlayerTrack } from '@/music/types';

interface ReplacementResultProps {
  items: ReplacementItem[];
}

export const ReplacementResult = ({ items }: ReplacementResultProps) => (
  <div className="flex w-full flex-col gap-6">
    {items.map((item, index) => (
      <div className="flex flex-col gap-2" key={index}>
        <ReplacementCard item={item} index={index} />
        <p className="text-base">{item.explanation}</p>
      </div>
    ))}
  </div>
);

type PlayMode = 'full' | 'segment';

const ReplacementCard = ({ item, index }: { item: ReplacementItem; index: number }) => {
  const [playMode, setPlayMode] = useState<PlayMode>('segment');
  const fullAudio = item.music.audios.find((a) => a.type === 'full') ?? item.music.audios[0];
  const title = `교체 추천 음원 ${index + 1}`;

  const track: PlayerTrack =
    playMode === 'segment'
      ? {
          url: fullAudio.url,
          title: `${title} (구간)`,
          generation: null,
          startTime: item.replacement_start,
          endTime: item.replacement_end,
        }
      : { url: fullAudio.url, title, generation: null };

  const isTrackPlaying = useAudioPlayerStore(
    (state) => state.activeTrack != null && isSameTrack(state.activeTrack, track) && state.isPlaying
  );
  const play = useAudioPlayerStore((state) => state.play);
  const pause = useAudioPlayerStore((state) => state.pause);

  const options = [
    { value: 'full' as const, label: '전체' },
    {
      value: 'segment' as const,
      label: `구간 ${formatTime(item.replacement_start)}~${formatTime(item.replacement_end)}`,
    },
  ];

  return (
    <div className="flex w-full items-center gap-3 rounded-xl border bg-white p-3">
      <PlayPauseButton
        isPlaying={isTrackPlaying}
        onClick={() => (isTrackPlaying ? pause() : play(track))}
      />
      <div className="flex min-w-0 grow flex-col gap-1">
        <p className="truncate text-sm font-medium">{title}</p>
        <AudioTypeSelector value={playMode} onChange={setPlayMode} options={options} />
      </div>
      <DownloadButton music={item.music} />
    </div>
  );
};
